import React, { useState, useRef, useCallback, useEffect } from 'react';
import './EditYAxisModal.scss';
import { ReactComponent as ReverseLeftBlue } from '../../images/blue-left-icon.svg';

interface YAxisData {
  id: string;
  label: string;
  defaultMin: number;
  defaultMax: number;
  unit?: string;
}

interface RangeSliderProps {
  data: YAxisData;
  index: number;
  onValueChange: (id: string, min: number, max: number) => void;
  onReset: () => void;
  resetSignal: number;
  minValue?: number;
  maxValue?: number;
  disabled?: boolean;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  data,
  index,
  onValueChange,
  onReset,
  resetSignal,
  minValue: propMinValue,
  maxValue: propMaxValue,
  disabled = false,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  const [minValue, setMinValue] = useState(propMinValue ?? data.defaultMin);
  const [maxValue, setMaxValue] = useState(propMaxValue ?? data.defaultMax);
  const [minInput, setMinInput] = useState(minValue.toString());
  const [maxInput, setMaxInput] = useState(maxValue.toString());
  const [minError, setMinError] = useState('');
  const [maxError, setMaxError] = useState('');
  const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);
  // Calculate the absolute range ONCE based on initial values
  const computeCenteredRange = (min: number, max: number) => {
    const range = max - min;
    const extension = range / 2;
    return {
      absoluteMin: min - extension,
      totalRange: range + extension * 2,
    };
  };

  const initialMin = typeof propMinValue === 'number' ? propMinValue : data.defaultMin;
  const initialMax = typeof propMaxValue === 'number' ? propMaxValue : data.defaultMax;
  const { absoluteMin: initialAbsoluteMin, totalRange: initialTotalRange } = computeCenteredRange(
    initialMin,
    initialMax,
  );

  const [absoluteMin, setAbsoluteMin] = useState(initialAbsoluteMin);
  const [totalRange, setTotalRange] = useState(initialTotalRange);

  useEffect(() => {
    // Only reset when resetSignal changes
    setMinValue(data.defaultMin);
    setMaxValue(data.defaultMax);
    setMinInput(data.defaultMin.toString());
    setMaxInput(data.defaultMax.toString());
    setMinError('');
    setMaxError('');
    setAbsoluteMin(initialAbsoluteMin);
    setTotalRange(initialTotalRange);
  }, [resetSignal]);

  // Sync with controlled props
  useEffect(() => {
    if (typeof propMinValue === 'number' && typeof propMaxValue === 'number') {
      setMinValue(propMinValue);
      setMaxValue(propMaxValue);
      setMinInput(propMinValue.toString());
      setMaxInput(propMaxValue.toString());
      setMinError('');
      setMaxError('');
    }
  }, [propMinValue, propMaxValue]);

  const validateAndUpdate = (type: 'min' | 'max', value: string) => {
    const numValue = Number(value);

    if (isNaN(numValue)) return;

    if (type === 'min') {
      if (numValue >= maxValue) return setMinError('Invalid Entry');
      setMinValue(numValue);
      setMinInput(value);
      setMinError('');
      onValueChange(data.id, numValue, maxValue);

      const { absoluteMin, totalRange } = computeCenteredRange(numValue, maxValue);

      setAbsoluteMin(absoluteMin);
      setTotalRange(totalRange);
    } else {
      if (numValue <= minValue) return setMaxError('Invalid Entry');

      setMaxValue(numValue);
      setMaxInput(value);
      setMaxError('');
      onValueChange(data.id, minValue, numValue);

      const { absoluteMin, totalRange } = computeCenteredRange(minValue, numValue);

      setAbsoluteMin(absoluteMin);
      setTotalRange(totalRange);
    }
  };

  const handleInputChange = (type: 'min' | 'max', value: string) => {
    const num = Number(value);

    const isValid = !isNaN(num) && (type === 'min' ? num < maxValue : num > minValue);

    if (type === 'min') {
      setMinInput(value);
      setMinError(isValid ? '' : 'Invalid Entry');
    } else {
      setMaxInput(value);
      setMaxError(isValid ? '' : 'Invalid Entry');
    }
  };

  const handleInputBlur = (type: 'min' | 'max') => {
    if (type === 'min') {
      validateAndUpdate('min', minInput);

      if (minError || isNaN(Number(minInput)) || Number(minInput) >= maxValue) {
        setMinInput(minValue.toString());
        setMinError('');
      }
    } else {
      validateAndUpdate('max', maxInput);

      if (maxError || isNaN(Number(maxInput)) || Number(maxInput) <= minValue) {
        setMaxInput(maxValue.toString());
        setMaxError('');
      }
    }
  };

  const getPositionFromValue = (value: number) => ((value - absoluteMin) / totalRange) * 100;
  const getValueFromPosition = (percentage: number) => Math.round(absoluteMin + (percentage / 100) * totalRange);

  const handleMouseDown = useCallback(
    (type: 'min' | 'max') => (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(type);
    },
    [],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !sliderRef.current) return;

      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const newValue = getValueFromPosition(percentage);

      if (isDragging === 'min' && newValue < maxValue) {
        setMinValue(newValue);
        setMinInput(newValue.toString());
        setMinError('');
        onValueChange(data.id, newValue, maxValue);
      } else if (isDragging === 'max' && newValue > minValue) {
        setMaxValue(newValue);
        setMaxInput(newValue.toString());
        setMaxError('');
        onValueChange(data.id, minValue, newValue);
      }
    },
    [isDragging, minValue, maxValue, absoluteMin, totalRange, data.id, onValueChange],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const minPosition = getPositionFromValue(minValue);
  const maxPosition = getPositionFromValue(maxValue);

  return (
    <div className={`range-slider range-slider--${index}`}>
      <div className='range-slider__header'>
        <h3 className='range-slider__label'>{data.label}</h3>
      </div>
      <div className='range-slider__controls'>
        <div className='range-slider__input-group'>
          <div className='range-slider__input-wrapper'>
            <div className={`range-slider__input ${minError && 'range-slider__input--error'}`}>
              <input
                type='text'
                className='range-slider__input-field'
                value={minInput}
                onChange={(e) => handleInputChange('min', e.target.value)}
                onBlur={() => handleInputBlur('min')}
                placeholder='Min'
                disabled={disabled}
              />
            </div>

            <div
              className='range-slider__track-container'
              ref={sliderRef}
              style={disabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}
            >
              <div className='range-slider__track-background' />
              <div
                className='range-slider__track-fill'
                style={{
                  left: `${minPosition}%`,
                  width: `${maxPosition - minPosition}%`,
                }}
              />
              <div
                className='range-slider__handle range-slider__handle--min'
                style={{ left: disabled ? '50%' : `${minPosition}%` }}
                onMouseDown={disabled ? undefined : handleMouseDown('min')}
              />
              <div
                className='range-slider__handle range-slider__handle--max'
                style={{ left: disabled ? '50%' : `${maxPosition}%` }}
                onMouseDown={disabled ? undefined : handleMouseDown('max')}
              />
            </div>

            <div className={`range-slider__input range-slider__input--max ${maxError && 'range-slider__input--error'}`}>
              <input
                type='text'
                className='range-slider__input-field'
                value={maxInput}
                onChange={(e) => handleInputChange('max', e.target.value)}
                onBlur={() => handleInputBlur('max')}
                placeholder='Max'
                disabled={disabled}
              />
            </div>
          </div>
          <div className='reset-container reset-container-custom cursor-pointer' onClick={onReset}>
            <ReverseLeftBlue />
            <h4 className='reset-default-header text-blue'>Reset</h4>
          </div>
        </div>
        <div className='range-slider__labels'>
          <span className='range-slider__label-text range-slider__label-text--min'>
            {minError ? <div className='range-slider__input-error'>{minError}</div> : 'Minimum'}
          </span>

          <span className='range-slider__label-text range-slider__label-text--max'>
            {maxError ? <div className='range-slider__input-error'>{maxError}</div> : 'Maximum'}
          </span>
        </div>
      </div>
    </div>
  );
};
